/**
 * Responsibility: lay out a railway as a chain of stations from a seed.
 *
 * ## Why a chain, when nothing else in this world accumulates
 *
 * Terrain is a pure function of `(seed, x, z)` and holds no state, which is what makes an infinite world
 * free. A railway breaks that rule in one specific way: its height at any point is the integral of its
 * gradient from wherever it started, so it genuinely is path-dependent.
 *
 * That turns out not to matter, because **the track is one-dimensional.** Replaying the chain from zero is
 * O(stations), not O(world area) — a hundred thousand stations of trivial arithmetic is microseconds. So the
 * chain is walked once, cached, and extended on demand, and the whole thing remains a pure function of the
 * seed. Determinism is untouched; a save is a distance along the chain.
 *
 * The alternative considered and rejected was a track defined as the zero-crossing contour of a noise field,
 * the way rivers and canyons are. It would have been memory-free and it gave branching for nothing, but its
 * height could not be constrained and a contour is as likely to close into a loop as to go anywhere.
 *
 * ## What it surveys against
 *
 * The **natural** ground, never the corridor-shaped ground. The corridor is derived from the route, so a
 * route derived from the corridor would be circular. Callers hold two elevation samplers for this reason:
 * a natural one for surveying and a shaped one for drawing.
 *
 * ## What the rules are, and why there are so few
 *
 * Measured over three routes of 120,000 units: sweeping the ruling grade from 1.5% to 4%, the view from
 * 3,000 to 9,000, terrain-blind against steering toward low ground, and aiming at the mean ground ahead
 * against aiming at valley floors, **every variant put 93.8% to 95.5% of the route within shaping range.**
 * The simplest was the best. So the surveyor holds a grade limit, a curvature limit, a wander field and a
 * lookahead average, and nothing else.
 *
 * The one thing that did matter was steering on the *biome* field rather than on terrain height — see
 * `RAILWAY.STEER_REACH`. That is injected as a cost function rather than built in, so this module stays
 * ignorant of which biomes exist.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../world/noise.js';
import { RAILWAY } from '../config.js';

/** Clamp a value into a range. */
function clamp(value, low, high) {
  return value < low ? low : value > high ? high : value;
}

/**
 * Build a surveyor for a seed.
 *
 * @param {object} options
 * @param {number} options.seed - Base world seed.
 * @param {(x: number, z: number) => number} options.naturalGround - Undeformed blended elevation. **Not the
 *   shaped surface**, or the route would depend on the corridor that depends on the route.
 * @param {(x: number, z: number) => number} [options.cost] - How much the route wants to avoid a coordinate,
 *   in arbitrary units where larger is worse. Sampled at probe points thousands of units ahead, so it must be
 *   a smooth, long-wavelength quantity — a biome weight, not a terrain height. Omitted means no steering.
 * @param {(x: number, z: number) => number} [options.aimBelow] - How far under the surrounding ground the line
 *   should run, in world units. For gorge country, where the corridor carves a canyon around the route and the
 *   rail therefore has to sit below the plateau. Omitted means follow the ground.
 * @param {object} [options.tuning] - Overrides for the `RAILWAY` config block.
 * @param {{x: number, z: number, heading: number}} [options.start] - Where the line begins and which way it
 *   sets off. Defaults to the origin heading east.
 * @returns {object}
 */
export function createSurveyor({
  seed,
  naturalGround,
  cost,
  aimBelow = null,
  tuning: overrides = RAILWAY,
  start,
}) {
  const tuning = overrides === RAILWAY ? RAILWAY : { ...RAILWAY, ...overrides };

  if (typeof naturalGround !== 'function') {
    throw new Error('createSurveyor: naturalGround must be a function');
  }
  if (!(tuning.STATION_SPACING > 0)) {
    throw new Error('createSurveyor: STATION_SPACING must be positive');
  }
  if (!(tuning.MIN_RADIUS > 0)) {
    throw new Error('createSurveyor: MIN_RADIUS must be positive');
  }

  const spacing = tuning.STATION_SPACING;
  const maxCurvature = 1 / tuning.MIN_RADIUS;

  /*
    Drives the plan alignment. Sampled along *chain distance* rather than at a world coordinate, which is
    what makes the line wander at a consistent rate however it happens to be pointing — a field sampled in
    world space would turn tightly wherever the route doubled back into ground it had already crossed.
  */
  const wander = createFbm(seed, 'rail:wander', {
    octaves: 3,
    baseFrequency: 1 / tuning.WANDER_WAVELENGTH,
    persistence: 0.5,
    lacunarity: 2.11,
  });

  /*
    How much tighter than usual this stretch of line is allowed to turn.

    A separate field on a much longer wavelength than the wander, so a sharper stretch is a *place on the
    route* rather than a wobble — the alternative was raising the limit everywhere, which loses the contrast
    that makes a curve read as a curve at all. Most of the line gets nothing from this; see
    `RAILWAY.CURVE_TIGHTEN_SHARE`.
  */
  const tightening = createFbm(seed, 'rail:tighten', {
    octaves: 2,
    baseFrequency: 1 / tuning.CURVE_TIGHTEN_WAVELENGTH,
    persistence: 0.5,
    lacunarity: 2.11,
  });

  /**
   * Curvature limit at a distance along the line.
   *
   * Normally exactly `1 / MIN_RADIUS`, and up to `CURVE_TIGHTEN` above it on the occasional stretch. Since the
   * limit is what the wander is scaled by *and* clamped to, raising it makes the whole stretch turn harder
   * rather than adding a kink to the middle of it.
   */
  function curvatureLimitAt(distance) {
    if (!(tuning.CURVE_TIGHTEN > 0)) return maxCurvature;

    /*
      Ramped between two explicit levels rather than over "the top quarter of the range", which under-delivered
      by half. A two-octave field almost never reaches the ends of its nominal 0..1 span — measured, it tops out
      around 0.9 — so a ramp that only completed at 1.0 meant the configured increase was never actually reached.
      Naming both ends makes the realised increase match the configured one; see `RAILWAY.CURVE_TIGHTEN_FULL`.
    */
    const level = toUnitRange(tightening(distance, 0));
    const span = tuning.CURVE_TIGHTEN_FULL - tuning.CURVE_TIGHTEN_FROM;
    if (level <= tuning.CURVE_TIGHTEN_FROM || !(span > 0)) return maxCurvature;

    const amount = Math.min(1, (level - tuning.CURVE_TIGHTEN_FROM) / span);

    return maxCurvature * (1 + tuning.CURVE_TIGHTEN * amount);
  }

  const origin = start ?? { x: 0, z: 0, heading: 0 };

  /** The chain so far. Index 0 is the start; every entry is `spacing` further along than the last. */
  const stations = [];

  /**
   * Buckets of station indices by world cell, so "what is the nearest track to here" does not scan the chain.
   *
   * Cell size covers the corridor's reach in one step of neighbours, which is what lets a query look at a
   * three-by-three block and be sure it has seen every station that could matter.
   */
  const bucketSize = Math.max(tuning.CORRIDOR_REACH * 2, spacing * 4);
  const buckets = new Map();

  const bucketKey = (x, z) => `${Math.floor(x / bucketSize)},${Math.floor(z / bucketSize)}`;

  function fileStation(index) {
    const key = bucketKey(stations[index].x, stations[index].z);
    const list = buckets.get(key);
    if (list) list.push(index);
    else buckets.set(key, [index]);
  }

  /**
   * The level the profile aims for: the average natural ground over the lookahead, less whatever the country
   * ahead asks the line to sit below.
   *
   * That subtraction exists for gorge country, where the rail has to run far under the plateau or there is
   * nothing for the corridor to carve a gorge out of. **It is deliberately local.** Measured over three routes,
   * aiming low everywhere took the share of a route needing nothing but shaping from 95.2% down to 79.2%,
   * because a line below the general ground level is in cut the whole way.
   */
  function targetLevel(x, z, heading) {
    const samples = tuning.LOOKAHEAD_SAMPLES;
    let total = 0;
    let below = 0;

    for (let i = 1; i <= samples; i += 1) {
      const reach = (tuning.LOOKAHEAD * i) / samples;
      const px = x + Math.cos(heading) * reach;
      const pz = z + Math.sin(heading) * reach;

      total += naturalGround(px, pz);
      if (aimBelow !== null) below += aimBelow(px, pz);
    }

    return (total - below) / samples;
  }

  /**
   * How much the route wants to turn, from the wander field plus the steering cost.
   *
   * The cost is read at one probe to each side, well ahead. Their difference is a gradient of "how much do I
   * want to be over there", and leaning down it is the whole of the steering.
   */
  function curvatureAt(distance, x, z, heading) {
    const limit = curvatureLimitAt(distance);
    let curvature = wander(distance, 0) * limit;

    if (typeof cost === 'function') {
      const reach = tuning.STEER_REACH;
      const side = tuning.STEER_SIDE;

      const aheadX = x + Math.cos(heading) * reach;
      const aheadZ = z + Math.sin(heading) * reach;

      const left = cost(aheadX - Math.sin(heading) * side, aheadZ + Math.cos(heading) * side);
      const right = cost(aheadX + Math.sin(heading) * side, aheadZ - Math.cos(heading) * side);

      // Clamped before scaling, so an unbounded cost function cannot slam the alignment against its limit.
      curvature += clamp(right - left, -1, 1) * tuning.STEER_STRENGTH * limit;
    }

    return clamp(curvature, -limit, limit);
  }

  /** Add one station to the end of the chain. */
  function extend() {
    if (stations.length === 0) {
      stations.push({
        distance: 0,
        x: origin.x,
        z: origin.z,
        heading: origin.heading,
        height: naturalGround(origin.x, origin.z),
        grade: 0,
      });
      fileStation(0);
      return;
    }

    const last = stations[stations.length - 1];

    const wanted = clamp(
      (targetLevel(last.x, last.z, last.heading) - last.height) / tuning.LOOKAHEAD,
      -tuning.MAX_GRADE,
      tuning.MAX_GRADE,
    );

    const grade =
      last.grade + clamp(wanted - last.grade, -tuning.MAX_GRADE_CHANGE, tuning.MAX_GRADE_CHANGE);

    const heading = last.heading + curvatureAt(last.distance, last.x, last.z, last.heading) * spacing;

    stations.push({
      distance: last.distance + spacing,
      x: last.x + Math.cos(heading) * spacing,
      z: last.z + Math.sin(heading) * spacing,
      heading,
      height: last.height + grade * spacing,
      grade,
    });

    fileStation(stations.length - 1);
  }

  /** Walk the chain out to at least a distance from the start. */
  function walkTo(distance) {
    const wanted = Math.floor(distance / spacing) + 2;
    while (stations.length < wanted) extend();
  }

  return {
    /**
     * Walk the chain out to a distance and return every station up to it.
     *
     * @param {number} distance - World units along the line from the start.
     * @returns {Array<object>} Live reference to the internal chain; treat as read-only.
     */
    stationsTo(distance) {
      walkTo(distance);
      return stations;
    },

    /**
     * The nearest point on the track to a coordinate.
     *
     * Projected onto the segment between two stations rather than snapped to the nearer one, because the
     * corridor is shaped against this and a staircase of station-sized steps would show as a staircase in
     * the ground.
     *
     * **Only sees chain that has already been walked.** The caller has to walk far enough before shaping
     * terrain, or a tile would be built against a route that does not exist there yet and would disagree
     * with the same tile built later. That is the one invariant this module cannot enforce for itself.
     *
     * @param {number} x
     * @param {number} z
     * @returns {{lateral: number, height: number, heading: number, distance: number}|null} Null when no
     *   station is within a bucket of the point, which is the common case away from the line.
     */
    nearest(x, z) {
      const cellX = Math.floor(x / bucketSize);
      const cellZ = Math.floor(z / bucketSize);

      let best = null;
      let bestLateral = Infinity;

      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dz = -1; dz <= 1; dz += 1) {
          const list = buckets.get(`${cellX + dx},${cellZ + dz}`);
          if (!list) continue;

          for (const index of list) {
            /*
              Both segments touching this station, so a point beside a joint is measured against the joint
              rather than against whichever neighbour happened to be filed in this bucket.
            */
            for (const other of [index - 1, index + 1]) {
              if (other < 0 || other >= stations.length) continue;

              const a = stations[Math.min(index, other)];
              const b = stations[Math.max(index, other)];

              const runX = b.x - a.x;
              const runZ = b.z - a.z;
              const lengthSquared = runX * runX + runZ * runZ;
              if (lengthSquared <= 0) continue;

              const t = clamp(((x - a.x) * runX + (z - a.z) * runZ) / lengthSquared, 0, 1);

              const pointX = a.x + runX * t;
              const pointZ = a.z + runZ * t;
              const lateral = Math.hypot(x - pointX, z - pointZ);

              if (lateral < bestLateral) {
                bestLateral = lateral;
                best = {
                  lateral,
                  height: a.height + (b.height - a.height) * t,
                  heading: a.heading + (b.heading - a.heading) * t,
                  distance: a.distance + (b.distance - a.distance) * t,
                };
              }
            }
          }
        }
      }

      return best;
    },

    /**
     * Where the line is at a distance along it, interpolated between stations.
     *
     * What the train rides. Interpolated rather than snapped, because the rider's eye is on this: at a
     * 40-unit station spacing, snapping would step the camera forward in visible jumps and lurch its heading
     * at every joint.
     *
     * Curvature comes from the change in heading over the interval, which is what the cant is derived from.
     * Taken across the whole segment rather than differenced per frame, so it is a property of the track
     * rather than of how fast the train happens to be going.
     *
     * @param {number} distance - Along the line from the start, in world units. Walks the chain if needed.
     * @returns {{x: number, z: number, height: number, heading: number, curvature: number}}
     */
    poseAt(distance) {
      const clamped = Math.max(0, distance);
      walkTo(clamped + spacing);

      const index = Math.min(stations.length - 2, Math.floor(clamped / spacing));
      const a = stations[index];
      const b = stations[index + 1];

      const t = clamp((clamped - a.distance) / spacing, 0, 1);

      return {
        x: a.x + (b.x - a.x) * t,
        z: a.z + (b.z - a.z) * t,
        height: a.height + (b.height - a.height) * t,
        heading: a.heading + (b.heading - a.heading) * t,
        curvature: (b.heading - a.heading) / spacing,
      };
    },

    /** How much chain has been walked, in world units. */
    walkedTo() {
      return stations.length === 0 ? 0 : stations[stations.length - 1].distance;
    },

    /** Walk the chain out to a distance without returning it, for priming before terrain is built. */
    prepare(distance) {
      walkTo(distance);
    },
  };
}
